import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { InvoiceKind } from '@qlkho-v3/shared';

import { api } from './api';
import { deliveryStatusLabel, deliveryStatusTone } from './delivery-status';
import { CloseIcon, DebtIcon, LockIcon, PrintIcon, ReturnIcon } from './icons';
import { OutboundPrintSheet, type OutboundPrintData } from './outbound-print-sheet';
import { visibleProductDetailName } from './product-display';

interface DetailLine {
  id: string;
  productName: string;
  detailName: string;
  unitName?: string;
  sku?: string;
  quantity: number;
  price: number;
  salePrice?: number;
  discountPercent?: number;
  discountAmount?: number;
  total?: number;
  warehouseName?: string;
  shelf?: string;
  isGift?: boolean;
  availableQuantity?: number;
  stockCount?: number;
  expiryDate?: string;
}

interface PaymentHistory {
  id: string;
  invoiceId: string;
  paid: number;
  date: string;
  creatorName?: string;
}

interface DetailInvoice {
  id: string;
  sku: string;
  date: string;
  partnerName?: string;
  creatorName?: string;
  discountTypeName?: string;
  discount_value?: number;
  total: number;
  paid: number;
  paid_debt: number;
  refund?: number;
  remaining: number;
  vat?: number;
  profit?: number;
  shipperName?: string;
  saleName?: string;
  is_delivery?: number;
  deliveryDate?: string | null;
  note?: string;
  is_lock?: number;
  status?: number;
  updatedAt?: string;
}

interface ReturnHistory {
  id: string;
  date: string;
  oldInvoiceSku?: string;
  newInvoiceSku?: string;
  totalQuantity: number;
  lines: Array<{
    id: string;
    productName: string;
    detailName?: string;
    lotSku?: string;
    unitName?: string;
    quantity: number;
  }>;
}

interface DetailResponse {
  invoice: DetailInvoice;
  lines: DetailLine[];
  payments: PaymentHistory[];
  returns: ReturnHistory[];
}

function money(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(Number(value) || 0);
}

function dateTime(value: string): string {
  const [date = '', time = ''] = String(value ?? '').split(' ');
  const [year, month, day] = date.split('-');
  return `${[day, month, year].filter(Boolean).join('/')}${time ? ` ${time.slice(0, 5)}` : ''}`;
}

function lineTotal(line: DetailLine): number {
  if (line.isGift) return 0;
  if (line.total !== undefined && line.total !== null) return Number(line.total);
  const gross = Number(line.quantity) * Number(line.price);
  if (line.discountAmount !== undefined && line.discountAmount !== null) return Math.round(gross) - lineDiscountAmount(line);
  return Math.round(gross * (1 - Number(line.discountPercent ?? 0) / 100));
}

function lineDiscountAmount(line: DetailLine): number {
  if (line.isGift) return 0;
  if (line.discountAmount !== undefined && line.discountAmount !== null) return Number(line.discountAmount);
  return Math.round(Number(line.quantity) * Number(line.price) * Number(line.discountPercent ?? 0) / 100);
}

function lineDiscountPercent(line: DetailLine): string {
  const gross = Number(line.quantity) * Number(line.price);
  const percent = !line.isGift && gross > 0 && line.discountAmount !== undefined && line.discountAmount !== null
    ? Number(line.discountAmount) / gross * 100
    : Number(line.discountPercent ?? 0);
  return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 }).format(percent);
}

export function InvoiceDetailMeta({ invoice, outbound }: { invoice: DetailInvoice; outbound: boolean }) {
  return (
    <section className={`detail-meta ${outbound ? 'outbound-detail-meta' : 'inbound-detail-meta'}`}>
      <div><span>Mã chứng từ</span><strong className="detail-invoice-code">{invoice.sku}{invoice.is_lock ? <LockIcon className="detail-lock-icon" role="img" aria-label="Hóa đơn đã khóa" aria-hidden={false} /> : null}</strong></div>
      <div><span>{outbound ? 'Khách hàng' : 'Nhà phân phối'}</span><strong>{invoice.partnerName || 'Không xác định'}</strong></div>
      <div><span>Ngày chứng từ</span><strong>{dateTime(invoice.date)}</strong></div>
      <div><span>Người tạo</span><strong>{invoice.creatorName || '—'}</strong></div>
      <div><span>Loại CK hóa đơn</span><strong>{invoice.discountTypeName || 'Không áp dụng'}</strong></div>
      {outbound ? <><div><span>Nhân viên bán hàng</span><strong>{invoice.saleName || '—'}</strong></div><div><span>Người giao hàng</span><strong>{invoice.shipperName || '—'}</strong></div><div><span>Thời gian giao hàng dự kiến</span><strong>{invoice.deliveryDate ? dateTime(invoice.deliveryDate) : 'Chưa chọn'}</strong></div><div><span>Trạng thái giao hàng</span><strong className={`delivery delivery-${deliveryStatusTone(invoice.is_delivery)}`}>{deliveryStatusLabel(invoice.is_delivery)}</strong></div></> : null}
    </section>
  );
}

export function InvoiceDetailModal({
  kind,
  invoiceId,
  canPay,
  canReturn,
  autoPrint = false,
  onClose,
  onPay,
  onReturn
}: {
  kind: InvoiceKind;
  invoiceId: string;
  canPay: boolean;
  canReturn: boolean;
  autoPrint?: boolean;
  onClose: () => void;
  onPay: (invoice: { id: string; sku: string; partnerName: string; remaining: number; refund: number }) => void;
  onReturn: (invoiceId: string) => void;
}) {
  const outbound = kind === 'outbound';
  const [data, setData] = useState<DetailResponse | null>(null);
  const [error, setError] = useState('');
  const [printData, setPrintData] = useState<OutboundPrintData | null>(null);
  const [printPending, setPrintPending] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [printError, setPrintError] = useState('');
  const printed = useRef(false);
  const printController = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api<DetailResponse>(`/api/invoices/${kind}/${invoiceId}`, {}, controller.signal)
      .then(setData)
      .catch((reason) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Không thể tải chi tiết hóa đơn.');
      });
    return () => controller.abort();
  }, [invoiceId, kind]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [onClose]);

  const invoice = data?.invoice;
  const paid = Number(invoice?.paid ?? 0) + Number(invoice?.paid_debt ?? 0);

  const requestPrint = useCallback(async () => {
    if (!outbound) return;
    printController.current?.abort();
    const controller = new AbortController();
    printController.current = controller;
    setPrintLoading(true);
    setPrintError('');
    try {
      const result = await api<OutboundPrintData>(`/api/invoices/outbound/${invoiceId}/print`, {}, controller.signal);
      if (controller.signal.aborted) return;
      setPrintData(result);
      setPrintPending(true);
    } catch (reason) {
      if (controller.signal.aborted) return;
      setPrintLoading(false);
      setPrintError(reason instanceof Error ? reason.message : 'Không thể tải mẫu in hóa đơn.');
    }
  }, [invoiceId, outbound]);

  useEffect(() => {
    if (!autoPrint || !outbound || printed.current) return;
    const timer = window.setTimeout(() => {
      if (printed.current) return;
      printed.current = true;
      void requestPrint();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [autoPrint, outbound, requestPrint]);

  useEffect(() => () => printController.current?.abort(), []);

  useEffect(() => {
    if (!printPending || !printData) return;
    let active = true;
    const timer = window.setTimeout(async () => {
      const images = [...document.querySelectorAll<HTMLImageElement>('.outbound-print-sheet img')];
      await Promise.race([
        Promise.all(images.map((image) => image.decode().catch(() => undefined))),
        new Promise<void>((resolve) => window.setTimeout(resolve, 1500))
      ]);
      if (!active) return;
      window.print();
      setPrintPending(false);
      setPrintLoading(false);
    }, 0);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [printData, printPending]);

  return (
    <div className="drawer-backdrop" role="presentation">
      <section className="invoice-detail-modal" role="dialog" aria-modal="true" aria-labelledby="detail-title">
        <header className="drawer-header">
          <h2 id="detail-title">Chi tiết hóa đơn {outbound ? 'xuất' : 'nhập'}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Đóng"><CloseIcon /></button>
        </header>

        {!data && !error ? <div className="editor-loading"><span className="spinner" /> Đang tải…</div> : null}
        {error ? <div className="detail-error" role="alert">{error}</div> : null}
        {data && invoice ? (
          <div className="detail-layout">
            <InvoiceDetailMeta invoice={invoice} outbound={outbound} />

            <div className="detail-lines-wrap">
              <table className={`detail-lines ${outbound ? 'outbound-detail-lines' : 'inbound-detail-lines'}`}>
                <thead><tr><th>Sản phẩm</th><th>Số lượng</th><th>ĐVT</th><th>{outbound ? 'Giá bán' : 'Giá nhập'}</th>{!outbound ? <th>Giá bán</th> : null}<th>CK %</th>{outbound ? <th>Tiền CK</th> : null}{!outbound ? <th>Kho / kệ</th> : null}<th>Thành tiền</th></tr></thead>
                <tbody>{data.lines.map((line) => (
                  <tr key={line.id}>
                    <td className="detail-product"><strong>{line.productName}</strong><div className="detail-product-badges">{line.isGift ? <span className="product-badge gift-badge">Quà tặng</span> : null}{visibleProductDetailName(line.productName, line.detailName) ? <span className="product-badge variant-badge" title={`Phân loại: ${line.detailName}`}>{line.detailName}</span> : null}{line.sku ? <span className="product-badge lot-badge" title={`${line.sku}${line.expiryDate ? ` · HSD: ${line.expiryDate}` : ''}`}><span>{line.sku}{line.expiryDate ? ` · HSD ${line.expiryDate}` : ''}</span></span> : null}</div></td>
                    <td className="align-right detail-quantity">{money(line.quantity)}</td>
                    <td className="detail-unit">{line.unitName || '—'}</td>
                    <td className="align-right">{money(line.price)} ₫</td>
                    {!outbound ? <td className="align-right">{money(line.salePrice ?? 0)} ₫</td> : null}
                    <td className="align-right">{outbound ? lineDiscountPercent(line) : money(line.discountPercent ?? 0)}%</td>
                    {outbound ? <td className="align-right discount-amount">{money(lineDiscountAmount(line))} ₫</td> : null}
                    {!outbound ? <td>{line.warehouseName || '—'}{line.shelf ? ` / ${line.shelf}` : ''}</td> : null}
                    <td className="align-right line-total">{money(lineTotal(line))} ₫</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>

            <section className={`detail-bottom ${data.returns.length ? 'has-return-history' : ''}`}>
              <div className="detail-note"><span>Ghi chú</span><p>{invoice.note || '—'}</p></div>
              <div className="payment-history">
                  <span>{outbound ? 'Lịch sử thu / hoàn tiền' : 'Lịch sử trả nợ'}</span>
                  <div className="payment-history-list">{data.payments.length ? data.payments.map((payment) => <p key={payment.id}><time>{dateTime(payment.date)}</time><b className={payment.paid < 0 ? 'refund-payment' : ''}>{payment.paid < 0 ? 'Hoàn ' : ''}{money(Math.abs(payment.paid))} ₫</b><em>{payment.creatorName || '—'}</em></p>) : <p className="no-payment">Chưa phát sinh thanh toán</p>}</div>
                  {data.returns.length ? (
                    <div className="return-history">
                      <span>Lịch sử trả hàng</span>
                      <div className="return-history-list">
                        {data.returns.map((item) => (
                          <article className="return-history-entry" key={item.id}>
                            <header>
                              <time>{dateTime(item.date)}</time>
                              <strong>{item.oldInvoiceSku === item.newInvoiceSku ? item.oldInvoiceSku : `${item.oldInvoiceSku} → ${item.newInvoiceSku}`}</strong>
                              <b>{money(item.totalQuantity)} SP</b>
                            </header>
                            <table>
                              <thead><tr><th>Sản phẩm / lô</th><th>Số lượng</th></tr></thead>
                              <tbody>{item.lines.map((line) => (
                                <tr key={line.id}>
                                  <td className="return-history-product">
                                    <strong title={line.productName}>{line.productName}</strong>
                                    {visibleProductDetailName(line.productName, line.detailName) || line.lotSku ? (
                                      <div className="detail-product-badges return-history-badges">
                                        {visibleProductDetailName(line.productName, line.detailName) ? <span className="product-badge variant-badge" title={`Phân loại: ${line.detailName}`}>{line.detailName}</span> : null}
                                        {line.lotSku ? <span className="product-badge lot-badge" title={line.lotSku}>{line.lotSku}</span> : null}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td><b>{money(line.quantity)}</b> <span>{line.unitName || 'SP'}</span></td>
                                </tr>
                              ))}</tbody>
                            </table>
                          </article>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              <div className="detail-totals">
                {outbound ? <p><span>Tiền CK sản phẩm</span><b>{money(data.lines.reduce((sum, line) => sum + lineDiscountAmount(line), 0))} ₫</b></p> : null}
                <p><span>Tổng tiền</span><b>{money(invoice.total)} ₫</b></p>
                <p><span>Đã thanh toán</span><b>{money(paid)} ₫</b></p>
                <p className="remaining"><span>Còn lại</span><b>{money(invoice.remaining)} ₫</b></p>
                {Number(invoice.refund ?? 0) > 0 ? <p className="refund-total"><span>Cần hoàn khách</span><b>{money(Number(invoice.refund))} ₫</b></p> : null}
              </div>
            </section>
          </div>
        ) : null}

        {printError ? <div className="return-error" role="alert">{printError}</div> : null}
        <footer className="drawer-footer">
          {invoice && data && canReturn && !invoice.is_lock && Number(invoice.status ?? 0) === 0 && data.lines.some((line) => outbound ? Number(line.quantity) > 0 : Number(line.availableQuantity ?? 0) > 0 && Number(line.stockCount ?? 0) === 1) ? <button type="button" className="secondary-action return-action" onClick={() => onReturn(invoice.id)}><ReturnIcon /> {outbound ? 'Khách trả hàng' : 'Trả hàng'}</button> : null}
          {invoice && canPay && (invoice.remaining > 0 || Number(invoice.refund ?? 0) > 0) ? <button type="button" className="primary-action" onClick={() => onPay({ id: invoice.id, sku: invoice.sku, partnerName: invoice.partnerName || 'Không xác định', remaining: Number(invoice.remaining), refund: Number(invoice.refund ?? 0) })}><DebtIcon /> {Number(invoice.refund ?? 0) > 0 ? 'Hoàn tiền' : outbound ? 'Thu nợ' : 'Trả nợ'}</button> : null}
          {invoice && outbound ? <button type="button" className="secondary-action print-action" disabled={printLoading} onClick={() => void requestPrint()}>{printLoading ? <span className="button-spinner" /> : <PrintIcon />} {printLoading ? 'Đang chuẩn bị…' : 'In hóa đơn'}</button> : null}
          <button type="button" className="secondary-action" onClick={onClose}>Đóng</button>
        </footer>
      </section>
      {printData ? createPortal(<OutboundPrintSheet data={printData} />, document.body) : null}
    </div>
  );
}
